import { PropsWithChildren, useState } from 'react'

import { useRequest } from 'ahooks'
import { Image } from 'antd'
import { createStyles } from 'antd-style'
import { isNil } from 'lodash'

import { queryOcrLimitApi } from '@eastcoal/api/nexus/ocr/queryOcrLimit'

import { nextTick } from '../../utils/nextTick'
import { UploadFile } from '../UploadFile'
import { UploadItem } from '../UploadFile/hooks/useResult/interface'

import { UploadOcrProps } from './interface'

const useStyles = createStyles(({ token }) => ({
  uploadFile: {
    display: 'block',
    [`&>div`]: {
      width: '100%'
    }
  },
  imgWrapper: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    cursor: 'pointer',
    borderRadius: token.borderRadius,
    border: `1px solid ${token.colorBorderSecondary}`,
    [`& img`]: {
      width: '100%',
      height: '100%'
    }
  },
  help: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    paddingBlock: token.paddingXXS,
    textAlign: 'center',
    color: token.colorTextLightSolid,
    backgroundColor: token.colorBgMask,
    fontSize: token.fontSizeSM
  }
}))

export const UploadOcr = ({
  className,
  style,
  imageStyle,
  help,
  scene,
  ocrApi,

  value,
  onChange,
  onOcrError,
  onLimitChange,

  children,
  /* -------------------------------------------------------------------------- */
  /*                              UploadFile Props                              */
  /* -------------------------------------------------------------------------- */
  disabled
}: PropsWithChildren<UploadOcrProps>) => {
  const [file, setFile] = useState<UploadItem>()
  const handleUploadDetail = (fileList: UploadItem[]) => {
    nextTick(() => setFile(fileList[0]))
  }

  const imageDom = file ? <Image src={file.url} style={imageStyle} /> : children

  const ossId = value?.ossId
  const uploadValue = ossId ? [ossId] : undefined
  const { refresh: refreshLimit } = useRequest(
    () => queryOcrLimitApi({ scene }),
    {
      manual: false,
      ready: !isNil(scene) && !disabled,
      refreshDeps: [scene],
      onSuccess: onLimitChange
    }
  )

  const handleUploadChange = async (value?: string[]) => {
    const ossId = value?.[0]
    if (!ossId) {
      onChange?.(undefined)
      return
    }
    try {
      const result = await ocrApi({ ossId, scene }, { silence: true })
      onChange?.({
        ossId,
        result: JSON.parse(result)
      })
    } catch (e: any) {
      onOcrError?.(e)
    } finally {
      refreshLimit()
    }
  }

  const { cx, styles } = useStyles()

  return (
    <UploadFile
      accept='image/*'
      className={styles.uploadFile}
      disabled={disabled}
      listType='text'
      multiple={false}
      openFileDialogOnClick={!file}
      value={uploadValue}
      onChange={handleUploadChange}
      onDetail={handleUploadDetail}
    >
      <div className={cx(styles.imgWrapper, className)} style={style}>
        {imageDom}
        {help && !file && !disabled && (
          <div className={styles.help}>{help}</div>
        )}
      </div>
    </UploadFile>
  )
}
